import React, { FC, ReactNode, useMemo, useState } from 'react';

import { Icon } from '@iconify/react';
import { Drawer } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import IconButton from '~/components/atoms/buttons/IconButton';
import InfoLabel from '~/components/atoms/InfoLabel';
import Logo from '~/components/atoms/Logo';
import MainLayoutSidebarMobile from '~/components/layouts/MainLayout/components/MainLayoutSidebar/Mobile';
import { SideBarItemType } from '~/core/sidebar';
import projectConfig, { PROJECT } from '~/projects/config';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { checkIsSubUnlimited } from '~/utils/subscription';

import styles from './styles.module.scss';

type Props = {
  sidebarGroups: SideBarItemType[][];
  children?: React.ReactNode;
  renderHeader?: (onClickOpen: () => void) => ReactNode;
  renderSidebar?: (onClickClose: () => void) => ReactNode;
};

const MobileSidebarHeader: FC<Props> = ({
  children,
  sidebarGroups,
  renderHeader,
  renderSidebar,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const openSidebar = (): void => setIsOpen(true);
  const closeSidebar = (): void => setIsOpen(false);

  const { t } = useTranslation('general');

  const profile = useAppSelector((state) => state.profile);

  const isShownBurgerNotify = useMemo(() => {
    return Boolean(
      sidebarGroups.find((group) =>
        group.find((item) => item.getLabel && item.getLabel(t) === '1'),
      ),
    );
  }, [sidebarGroups]);

  return (
    <>
      <div className={styles.main}>
        {renderHeader ? (
          renderHeader(openSidebar)
        ) : (
          <div className={styles.header}>
            <div className={styles.header__burger_button}>
              <IconButton onClick={openSidebar} size="small">
                <Icon icon="solar:hamburger-menu-linear" />
              </IconButton>
              {isShownBurgerNotify && (
                <InfoLabel
                  className={styles.header__burger_button__label}
                  text={'1'}
                />
              )}
            </div>

            <div className={styles.header__logo_wrapper}>
              <div className={styles.header__logo}>
                <Logo />
              </div>

              {checkIsSubUnlimited(
                profile.is_stripe_user,
                profile.user_subscription.is_trial,
                profile.user_product.regularity,
              ) && (
                <div
                  className={clsx(styles.header__unlimited_label, {
                    [styles['header__unlimited_label--sidekicker']]:
                      projectConfig.project === PROJECT.SIDEKICKER,
                  })}
                >
                  Unlimited
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.content}>{children}</div>
      </div>

      <Drawer
        anchor="left"
        open={isOpen}
        onClose={closeSidebar}
        PaperProps={{
          className: renderSidebar ? styles.sidebar_panel : undefined,
        }}
      >
        {renderSidebar ? (
          renderSidebar(closeSidebar)
        ) : (
          <div className={styles.sidebar}>
            <MainLayoutSidebarMobile
              items={sidebarGroups}
              onClickClose={closeSidebar}
            />
          </div>
        )}
      </Drawer>
    </>
  );
};

export default MobileSidebarHeader;
